/* eslint-disable react/no-unknown-property */
import { css } from '@emotion/react';
import { getLanguage } from 'i18n';
import { useEffect, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';

import { LegacyCard } from './LegacyCard';
import { theme } from './theme';

const styles = {
  wrapper: css`
    position: relative;
  `,
  paymentContainer: (ready: boolean) => css`
    outline: 1px solid ${theme.colors.border};
    border-radius: ${theme.borders.radius.lg};
    min-height: 344px;
    padding-top: ${theme.spacing.sm};
    background: white;
    opacity: ${ready ? '1' : '0'};
  `,
};

type PaymentSDKProps = {
  reference: string | undefined;
  isProduction: boolean;
  showSavePaymentMethod?: boolean;
  showHeader?: boolean;
  handleSuccess: VoidFunction;
  handleError: (reason?: string) => void;
  handleRefused: (message: string) => void;
  handleCancel?: VoidFunction;
  handlePaymentUpdated?: (detail: PaymentUpdatedDetail) => void;
  handleSessionExpired?: VoidFunction;
  handlePaymentReview?: (reviewing: boolean) => void;
  handlePending: VoidFunction;
  logException: (error: unknown) => void;
  Skeleton?: React.FunctionComponent<{ className?: string }>;
  className?: string;
};

export const PaymentSDK = ({
  reference,
  isProduction,
  handleSuccess,
  handleCancel,
  handleError,
  handleRefused,
  handlePaymentUpdated,
  handleSessionExpired,
  handlePaymentReview,
  handlePending,
  logException,
  showSavePaymentMethod = false,
  showHeader = true,
  Skeleton = PaymentSDKSkeleton,
  className,
}: PaymentSDKProps) => {
  const [isPaymentSDKReady, setIsPaymentSDKReady] = useState(false);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);

  const completedRef = useRef(false);
  const isPaymentSDKInitiatedRef = useRef(false);

  const sdkWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadPaymentScript(isProduction)
      .then(() => {
        setIsScriptLoaded(true);
      })
      .catch((error: unknown) => {
        logException(error);
        handleError();
      });
  }, []);

  useEffect(() => {
    if (isScriptLoaded && reference && !isPaymentSDKInitiatedRef.current) {
      if (!document.getElementById(rootID) && sdkWrapperRef.current) {
        const container = document.createElement('div');
        container.id = rootID;
        container.setAttribute('payment-reference', reference);
        container.setAttribute('show-save-payment-method', showSavePaymentMethod ? 'true' : 'false');
        container.setAttribute('no-header', showHeader ? 'false' : 'true');

        sdkWrapperRef.current.appendChild(container);
      }

      document.dispatchEvent(new Event(PaymentEvent.RENDER, { bubbles: true }));

      isPaymentSDKInitiatedRef.current = true;
    }
  }, [isScriptLoaded, reference, showSavePaymentMethod, showHeader]);

  useEffect(() => {
    const handlePaymentStateChange = (evnt: Event & { detail?: PaymentStatus }) => {
      if (completedRef.current || !evnt.detail) {
        return;
      }

      const { errors, pending } = evnt.detail;
      const hasErrors = errors?.length && errors.length > 0;

      if (pending) {
        handlePending();
      } else if (hasErrors) {
        if (errors.length === 1 && errors[0].errorCode === ErrorCode.ALREADY_PAYED) {
          completedRef.current = true;
          handleSuccess();
        } else {
          logException(new Error('Unknown payment state change error', { cause: errors }));
          handleError();
        }
      }
    };

    document.addEventListener(PaymentEvent.STATE_CHANGE, handlePaymentStateChange, { capture: true });
    return () => {
      document.removeEventListener(PaymentEvent.STATE_CHANGE, handlePaymentStateChange, { capture: true });
    };
  }, [handlePending, handleError, handleSuccess]);

  useEffect(() => {
    const handlePaymentEvent = (event: Event & { detail?: PaymentResult }) => {
      if (completedRef.current || !event.detail) {
        return;
      }

      const detail = event.detail;

      switch (detail.resultCode) {
        case PaymentResultStatus.SUCCEEDED:
          handleSuccess();
          break;
        case PaymentResultStatus.FAILED: {
          const errorCode = detail.resultDetails.errorCode;
          const message = detail.resultDetails.message;

          if (errorCode === ErrorCode.REFUSED_ERROR) {
            handleRefused(message);
          } else {
            logException(new Error('Unknown payment completed error', { cause: { errorCode, message } }));
            handleError(message);
          }
          break;
        }
        case PaymentResultStatus.CANCELLED: {
          if (handleCancel) {
            handleCancel();
          }
          break;
        }
      }
    };

    document.addEventListener(PaymentEvent.COMPLETED, handlePaymentEvent, { capture: true });
    return () => {
      document.removeEventListener(PaymentEvent.COMPLETED, handlePaymentEvent, { capture: true });
    };
  }, [handleSuccess, handlePending, handleError, handleRefused]);

  useEffect(() => {
    if (!handlePaymentUpdated) return;

    const handlePaymentUpdateEvent = (event: Event & { detail?: PaymentUpdatedDetail }) => {
      if (completedRef.current || !event.detail) {
        return;
      }

      handlePaymentUpdated(event.detail);
    };

    document.addEventListener(PaymentEvent.PAYMENT_UPDATED, handlePaymentUpdateEvent, { capture: true });
    return () => {
      document.removeEventListener(PaymentEvent.PAYMENT_UPDATED, handlePaymentUpdateEvent, { capture: true });
    };
  }, [handlePaymentUpdated]);

  useEffect(() => {
    if (!handlePaymentReview) return;

    const handleReviewStarted = () => {
      handlePaymentReview(true);
    };

    document.addEventListener(PaymentEvent.REVIEW_STARTED, handleReviewStarted, { capture: true });
    return () => {
      document.removeEventListener(PaymentEvent.REVIEW_STARTED, handleReviewStarted, { capture: true });
    };
  }, [handlePaymentReview]);

  useEffect(() => {
    if (!handlePaymentReview) return;

    const handleReviewCompleted = () => {
      handlePaymentReview(false);
    };

    document.addEventListener(PaymentEvent.REVIEW_COMPLETED, handleReviewCompleted, { capture: true });
    return () => {
      document.removeEventListener(PaymentEvent.REVIEW_COMPLETED, handleReviewCompleted, { capture: true });
    };
  }, [handlePaymentReview]);

  useEffect(() => {
    const handleReadyEvent = () => {
      setIsPaymentSDKReady(true);
    };

    document.addEventListener(PaymentEvent.READY, handleReadyEvent, { capture: true });
    return () => {
      document.removeEventListener(PaymentEvent.READY, handleReadyEvent, { capture: true });
    };
  }, [setIsPaymentSDKReady]);

  useEffect(() => {
    const handleReadyEvent = () => {
      if (handleSessionExpired) {
        handleSessionExpired();
      }
    };

    document.addEventListener(PaymentEvent.SESSION_EXPIRED, handleReadyEvent, { capture: true });
    return () => {
      document.removeEventListener(PaymentEvent.SESSION_EXPIRED, handleReadyEvent, { capture: true });
    };
  }, [handleSessionExpired]);

  return (
    <div css={styles.wrapper} className={className}>
      <div css={styles.paymentContainer(isPaymentSDKReady)} ref={sdkWrapperRef} />
      {!isPaymentSDKReady && <Skeleton css={skeletonStyles.wrapper} />}
    </div>
  );
};

const skeletonStyles = {
  wrapper: css`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
  `,
  container: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.md};
  `,
  paymentType: css`
    display: flex;
    justify-content: space-between;
  `,
  radio: css`
    display: flex;
    gap: ${theme.spacing.md};
  `,
};

const PaymentSDKSkeleton = ({ className }: { className?: string }) => {
  return (
    <LegacyCard css={skeletonStyles.container} className={className}>
      <div css={skeletonStyles.paymentType}>
        <div css={skeletonStyles.radio}>
          <Skeleton circle width={18} height={18} />
          <Skeleton width={80} height={18} />
        </div>
        <Skeleton width={60} height={40} borderRadius={0} />
      </div>
      <div css={skeletonStyles.paymentType}>
        <div css={skeletonStyles.radio}>
          <Skeleton circle width={18} height={18} />
          <Skeleton width={80} height={18} />
        </div>
        <Skeleton width={60} height={40} borderRadius={0} />
      </div>
      <div css={skeletonStyles.paymentType}>
        <div css={skeletonStyles.radio}>
          <Skeleton circle width={18} height={18} />
          <Skeleton width={80} height={18} />
        </div>
        <Skeleton width={60} height={40} borderRadius={0} />
      </div>
    </LegacyCard>
  );
};

const getPaymentSDKLocale = () => {
  const language = getLanguage();

  if (language === 'nb') {
    return 'no';
  }

  return language;
};

const rootID = 'pay-root';

export enum PaymentEvent {
  RENDER = 'payment:renderPayment',
  READY = 'payment:ready',
  PAY = 'payment:pay',
  COMPLETED = 'payment:onPaymentCompleted',
  STATE_CHANGE = 'payment:state',
  PAYMENT_UPDATED = 'payment:paymentUpdated',
  SESSION_EXPIRED = 'payment:paymentExpired',
  REVIEW_STARTED = 'payment:paymentReviewStarted',
  REVIEW_COMPLETED = 'payment:paymentReviewCompleted',
  HANDLE_REDIRECT = 'payment:handleRedirect',
}

export enum PaymentResultStatus {
  SUCCEEDED = 'SUCCEEDED',
  PENDING = 'PENDING',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
}

type PaymentResultDetails = {
  message: string;
  errorCode: ErrorCode;
};

type PaymentResult = {
  resultCode: PaymentResultStatus;
  resultDetails: PaymentResultDetails;
};

type PaymentStatus = {
  pending: boolean;
  errors: PaymentResultDetails[];
};

export type PaymentUpdatedDetail = {
  amountSummary: {
    originalAmount: number;
    amountCoveredByVouchers: number;
    amountCoveredByPoints: number;
    leftToPay: number;
  };
  vouchers?: { number: string; amount: number }[];
  cardFees?: { ref: string; amount: number }[];
};

export enum ErrorCode {
  ALREADY_PAYED = '101',
  TOO_MANY_ATTEMPTS_ERROR = '102',
  EXPIRED_ERROR = '103',
  REFUSED_ERROR = '108',
  GENERAL_ERROR = '109',
}

export function triggerPay() {
  setTimeout(() => {
    document.dispatchEvent(new Event(PaymentEvent.PAY, { bubbles: true }));
  }, 0);
}

let paymentSDKScriptPromise: Promise<void> | undefined;

const devUrl = 'https://payment-client.test.flysas.com';
const productionUrl = 'https://payment-client.flysas.com';

export function loadPaymentScript(isProduction: boolean): Promise<void> {
  const scriptUrl = `${isProduction ? productionUrl : devUrl}/saspayment.${getPaymentSDKLocale()}.js`;

  const url = isProduction ? scriptUrl : `${scriptUrl}?env=dev`;

  if (paymentSDKScriptPromise) {
    return paymentSDKScriptPromise;
  }

  paymentSDKScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = url;
    script.async = true;
    script.type = 'module';

    script.onload = () => resolve();
    script.onerror = reject;

    document.body.appendChild(script);
  });

  return paymentSDKScriptPromise;
}
